import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

export default function CompleteProfilePage() {
  const { updateUser, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading]       = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [form, setForm] = useState({
    name: '', gender: '',
    locationAddress: '', locationLat: '', locationLng: '',
    referredBy: ''
  });

  // ── Auto-patch referral code ─────────────────────────────
  // HashRouter: URL is zevazeva.com/#/complete-profile?ref=XXXX
  // location.search is empty — must read from window.location.hash
  useEffect(() => {
    // Try location.search first (BrowserRouter)
    let ref = new URLSearchParams(location.search).get('ref');

    // Fallback: parse from hash (HashRouter)
    if (!ref) {
      const hash = window.location.hash; // e.g. "#/complete-profile?ref=80E1341C"
      const hashSearch = hash.includes('?') ? hash.split('?')[1] : '';
      ref = new URLSearchParams(hashSearch).get('ref')
         || new URLSearchParams(hashSearch).get('referral')
         || new URLSearchParams(hashSearch).get('code');
    }

    if (ref) {
      const clean = ref.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
      setForm(p => ({ ...p, referredBy: clean }));
      toast.success(`Referral code "${clean}" applied! 🎉`);
    }
  }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const getGPS = () => {
    if (!navigator.geolocation) return toast.error('Geolocation not supported');
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        set('locationLat', pos.coords.latitude);
        set('locationLng', pos.coords.longitude);
        fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${pos.coords.latitude}&longitude=${pos.coords.longitude}&localityLanguage=en`)
          .then(r => r.json())
          .then(d => {
            const parts = [d.locality, d.city || d.principalSubdivision?.split(' ')[0], d.countrySubdivision]
              .filter(Boolean);
            const unique = [...new Set(parts)];
            if (unique.length) set('locationAddress', unique.join(', '));
          })
          .catch(() => {})
          .finally(() => setGpsLoading(false));
      },
      () => { setGpsLoading(false); toast.error('Could not get location'); },
      { timeout: 8000, enableHighAccuracy: true }
    );
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setLoading(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => !payload[k] && delete payload[k]);
      const { data } = await api.put('/auth/profile', payload);
      updateUser(data.user);
      toast.success('Profile saved! ✅');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving profile');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:'100vh', background:'var(--gray-50)', paddingBottom:'var(--sp-8)' }}>
      <div className="header">
        <div className="header__row">
          <div>
            <div className="header__title">Complete Profile</div>
            <div className="header__subtitle">Help farmers and buyers find you</div>
          </div>
        </div>
      </div>

      <div className="container" style={{ paddingTop:'var(--sp-5)' }}>
        <div className="card">
          <div className="card__body">
            <div className="form-group">

              <div>
                <label className="label">Full Name *</label>
                <input className="input" placeholder="e.g. Ramesh Kumar"
                  value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
              </div>

              <div>
                <label className="label">Gender</label>
                <select className="input" value={form.gender} onChange={e => set('gender', e.target.value)}>
                  <option value="">Select gender</option>
                  <option value="male">👨 Male</option>
                  <option value="female">👩 Female</option>
                  <option value="other">🧑 Other</option>
                  <option value="not_specified">🤐 Prefer not to say</option>
                </select>
              </div>

              <div>
                <label className="label">Location / Address</label>
                <div className="input-group">
                  <input className="input" placeholder="Village, District, State"
                    value={form.locationAddress} onChange={e => set('locationAddress', e.target.value)} />
                  <button className="btn btn--outline btn--sm" onClick={getGPS} disabled={gpsLoading}>
                    {gpsLoading
                      ? <span className="spinner spinner--sm" style={{ width:14, height:14 }} />
                      : ' GPS'}
                  </button>
                </div>
                {form.locationLat && <p className="hint hint--success">✓ GPS captured</p>}
              </div>

              <div>
                <label className="label">Referral Code (optional)</label>
                <input className="input"
                  placeholder="Enter friend's referral code"
                  maxLength={8}
                  value={form.referredBy}
                  onChange={e => set('referredBy', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,'').slice(0,8))}
                  style={{ background: form.referredBy ? '#DCFCE7' : '', fontWeight: form.referredBy ? 600 : 400, letterSpacing: form.referredBy ? 2 : 0 }}
                />
                {form.referredBy
                  ? <p className="hint hint--success">✅ Referral code applied — {form.referredBy}</p>
                  : <p className="hint">Share your referral link to auto-fill this</p>
                }
              </div>

            </div>
          </div>
        </div>

        <div style={{ marginTop:'var(--sp-5)' }}>
          <button className="btn btn--primary btn--lg" onClick={submit} disabled={loading}>
            {loading ? <span className="spinner spinner--sm spinner--white" /> : 'Save & Continue →'}
          </button>
        </div>
      </div>
    </div>
  );
}